import { createContext, useCallback, useContext, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { CreateSaleOrderDto, SaleOrderResponseDto } from '@bandido/shared/dtos';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { CONFIG } from '@frontend/config';

import { addSaleOrderItem, convertCartToSaleOrder, fetchSaleOrder, removeItemFromSaleOrder } from './services';
import { cartSaleOrderQueryKey } from '../../cartSession';

export const CartContext = createContext<{
  state?: SaleOrderResponseDto | null;
  isLoading: boolean;
  isSaving: boolean;
  createOrderIsLoading: boolean;
  addItem: (payload: CreateSaleOrderDto & { socketId: string }) => Promise<SaleOrderResponseDto>;
  removeItem: (itemId: string) => Promise<void>;
  refetch: () => Promise<SaleOrderResponseDto | undefined>;
  convert: () => Promise<SaleOrderResponseDto | null>;
    }>({
      state: null,
      isLoading: false,
      isSaving: false,
      createOrderIsLoading: false,
      refetch: async () => undefined,
      removeItem: async () => {},
      addItem: async () => ({}) as SaleOrderResponseDto,
      convert: async () => ({}) as SaleOrderResponseDto,
    });

interface Props {
  children: JSX.Element;
}

export const CartContextProvider = ({ children }: Props) => {
  const { addToast } = useSnackBar();
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();
  const { myProfile } = useAuth();
  const saleOrderQueryKey = cartSaleOrderQueryKey(
    CONFIG.AGENCY_ID,
    myProfile?.id,
  );
  // El carrito ya no es bootstrap de la aplicación. Solo el flujo hotelero
  // legacy pide explícitamente su orden activa mediante `refetch`. La cache
  // queda aislada por identidad para que no sobreviva a un cambio de sesión.
  const { data: state, isLoading, refetch: refetchQuery } = useQuery<SaleOrderResponseDto>(
    saleOrderQueryKey,
    () => fetchSaleOrder(),
    { enabled: false },
  );
  const queryClient = useQueryClient();
  const refetch = useCallback(async () => {
    if (!myProfile?.id) {
      throw new Error('Cart refresh requires an identified user');
    }
    const result = await refetchQuery();
    if (result.error) throw result.error;
    return result.data;
  }, [myProfile?.id, refetchQuery]);

  const addItemMutation = useMutation(
    (payload: CreateSaleOrderDto & { socketId: string }) => addSaleOrderItem(payload),
    {
      onSuccess: (saleOrder) => {
        queryClient.setQueryData(saleOrderQueryKey, saleOrder);
      },
    },
  );

  const removeItemMutation = useMutation((itemId: string) => removeItemFromSaleOrder(itemId), {
    onSuccess: async (_response, itemId) => {
      queryClient.setQueryData<SaleOrderResponseDto | undefined>(
        saleOrderQueryKey,
        (current) => current
          ? { ...current, items: current.items.filter((item) => item.id !== itemId) }
          : current,
      );
      try {
        await refetch();
      } catch (_error) {
        // El DELETE ya terminó; la cache local anterior evita fingir que falló.
      }
    },
  });

  const addItem = async (payload: CreateSaleOrderDto & { socketId: string }) => {
    return addItemMutation.mutateAsync(payload);
  };

  const removeItem = async (itemId: string) => {
    await removeItemMutation.mutateAsync(itemId);
  };

  const convertCartToSaleOrderMutation = useMutation((id: string) => convertCartToSaleOrder(id), {
    onSettled: () => {
      setIsSaving(false);
    },
    onMutate: () => {
      setIsSaving(true);
    },
    onSuccess: () => {
      queryClient.setQueryData(saleOrderQueryKey, null);
      addToast({
        color: 'success',
        message: t('cart.convert.success')
      });
    },
    onError: () => {
      addToast({
        color: 'error',
        message: t('cart.convert.error')
      });
    },
  });

  const convert = async () => {
    if(myProfile?.id && state?.id) {
      return convertCartToSaleOrderMutation.mutateAsync(state.id);
    }
    return null;
  };

  return (
    <CartContext.Provider
      value={{
        state: myProfile?.id ? state : undefined,
        isLoading,
        isSaving,
        addItem,
        removeItem,
        refetch,
        convert,
        createOrderIsLoading: addItemMutation.isLoading,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartContextProvider');
  }
  return context;
};
